import React, { useEffect, useMemo, useState } from "react";
import { Calendar as CalIcon, Users, MapPin, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { ImageCarousel, productImages } from "../components/ImageCarousel";

const CAT_LABELS = {
  "whiskey-tasting": "Whiskey Tasting", "cigar-tasting": "Cigar Tasting", "networking": "Networking",
  "live-music": "Live Music", "speaker": "Speaker Series", "poker": "Poker Night", "golf": "Golf Outing",
  "private-dinner": "Private Dinner",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const cats = useMemo(() => {
    const s = new Set(events.map((e) => e.category));
    return ["all", ...Array.from(s)];
  }, [events]);

  const filtered = useMemo(() => filter === "all" ? events : events.filter((e) => e.category === filter), [events, filter]);

  return (
    <div data-testid="events-page">
      <section className="pt-40 pb-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <p className="eyebrow">The Calendar</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
            Evenings, <span className="italic" style={{ color: "var(--twr-bronze)" }}>quietly remarkable.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/70 mt-6 max-w-2xl">
            Tastings, dinners, founders mixers, speaker evenings, and outings — built for the long-form conversation.
          </p>
        </div>
      </section>

      <section className="border-y border-[var(--twr-line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-[72px] z-30 py-5 px-6 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto flex flex-wrap gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`events-cat-${c}`}
              className={`pill ${filter === c ? "active" : ""}`}>
              {c === "all" ? "All" : (CAT_LABELS[c] || c)}
            </button>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((e) => (
            <article key={e.id} data-testid={`event-card-${e.id}`} className="surface overflow-hidden flex flex-col group">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={e.image} alt={e.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <p className="eyebrow">{CAT_LABELS[e.category] || e.category} {e.members_only && " · Members Only"}</p>
                <h3 className="font-serif text-2xl mt-4 leading-snug">{e.title}</h3>
                <p className="text-[var(--twr-warm-white)]/65 text-sm mt-3 line-clamp-3 flex-1">{e.description}</p>
                <div className="mt-6 space-y-2 text-[12px] text-[var(--twr-warm-white)]/70">
                  <div className="flex items-center gap-2"><CalIcon size={13} className="text-[var(--twr-bronze)]" /><span>{new Date(e.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit", year: "numeric" })}</span></div>
                  <div className="flex items-center gap-2"><MapPin size={13} className="text-[var(--twr-bronze)]" /><span>{e.location}</span></div>
                  <div className="flex items-center gap-2"><Users size={13} className="text-[var(--twr-bronze)]" /><span>{e.seats_remaining} of {e.seats_total} seats remaining</span></div>
                </div>
                <div className="flex items-center justify-between mt-7">
                  <span className="font-serif text-xl text-[var(--twr-bronze)]">{e.price > 0 ? `$${e.price.toFixed(0)}` : "Complimentary"}</span>
                  <ReserveDialog event={e} />
                </div>
              </div>
            </article>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center py-20 text-[var(--twr-warm-white)]/55">No events in this category yet.</p>}
      </section>
    </div>
  );
}

function ReserveDialog({ event }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={`event-reserve-${event.id}`} className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase border border-[var(--twr-bronze)] text-[var(--twr-bronze)] px-4 py-2.5 hover:bg-[var(--twr-bronze)] hover:text-[#0A0A0A] transition-colors">
          Reserve <ChevronRight size={12} />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-left">{event.title}</DialogTitle>
          <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">Event details and reservation information.</DialogDescription>
        </DialogHeader>
        {productImages(event).length > 0 && (
          <div className="aspect-[16/9] overflow-hidden">
            <ImageCarousel images={productImages(event)} alt={event.title} testId="event-dialog-carousel" />
          </div>
        )}
        <div className="space-y-3 text-sm text-[var(--twr-warm-white)]/75 leading-relaxed">
          <p>{event.description}</p>
          <div className="hairline my-4" />
          <p><span className="eyebrow mr-2">When</span> {new Date(event.date).toLocaleString("en-US")}</p>
          <p><span className="eyebrow mr-2">Where</span> {event.location}</p>
          <p><span className="eyebrow mr-2">Price</span> {event.price > 0 ? `$${event.price.toFixed(0)}` : "Complimentary"}</p>
        </div>
        <p className="text-[var(--twr-warm-white)]/55 text-[12px] mt-4">
          To reserve your seat, contact the concierge at <a href="mailto:concierge@thewinroomfl.com" className="text-[var(--twr-bronze)]">concierge@thewinroomfl.com</a> or call <a href="tel:+14075550100" className="text-[var(--twr-bronze)]">(407) 555-0100</a>.
        </p>
      </DialogContent>
    </Dialog>
  );
}
